import { FC, useEffect, useState, useCallback } from 'react';
import { Table, Button, message, Space, Modal, Tag } from 'antd';
import { fetchActiveSimulations, fetchAllSimulations, deleteSimulation, deactivateSimulation, clearSimulationPoints, SimulationResult, PriceSimulationWithPointCount } from '../../api/priceSimulation';
import TimeDisplay from '../TimeDisplay';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface SimulationHistoryListProps {
  simulations?: SimulationResult[];
  simulationsWithCount?: PriceSimulationWithPointCount[];
  onRefreshNeeded?: () => void;
  onView?: (simulation: SimulationResult) => void;
  showHistoryMode?: boolean;
}

const SimulationHistoryList: FC<SimulationHistoryListProps> = ({ 
  simulations: propSimulations, 
  simulationsWithCount: propSimulationsWithCount, 
  onRefreshNeeded, 
  onView, 
  showHistoryMode = false 
}) => {
  const [simulations, setSimulations] = useState<SimulationResult[]>([]);
  const [simulationsWithCount, setSimulationsWithCount] = useState<PriceSimulationWithPointCount[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSimulations = useCallback(async () => {
    if (propSimulations) {
      setSimulations(propSimulations);
      return;
    }
    if (propSimulationsWithCount) {
      setSimulationsWithCount(propSimulationsWithCount);
      return;
    }
    
    setLoading(true);
    try {
      if (showHistoryMode) {
        const data = await fetchAllSimulations();
        setSimulationsWithCount(data);
      } else {
        const data = await fetchActiveSimulations();
        setSimulations(data);
      }
    } catch (error) {
      console.error('获取价格模拟设置失败', error);
    } finally {
      setLoading(false);
    }
  }, [propSimulations, propSimulationsWithCount, showHistoryMode]);

  useEffect(() => {
    if (propSimulations) {
      setSimulations(propSimulations);
    } else if (propSimulationsWithCount) {
      setSimulationsWithCount(propSimulationsWithCount);
    } else {
      fetchSimulations();
    }
  }, [propSimulations, propSimulationsWithCount, fetchSimulations]);


  const handleDeactivate = async (productCode: string) => {
    try {
      await deactivateSimulation(productCode);
      message.success(`产品 ${productCode} 的价格模拟已取消激活`);
      refreshData();
    } catch (error) {
      console.error('取消激活失败', error);
      message.error(`取消激活产品 ${productCode} 的价格模拟失败，请稍后重试`);
    }
  };

  const handleClearPoints = async (simulationId: number, productCode: string) => {
    Modal.confirm({
      title: '确认清空模拟点数据',
      icon: <ExclamationCircleOutlined />,
      content: `确定要清空产品 ${productCode} 的所有关联点数据吗？此操作不可恢复！`,
      onOk: async () => {
        try {
          await clearSimulationPoints(simulationId);
          message.success(`产品 ${productCode} 的模拟点数据已清空`);
          refreshData();
        } catch (error) {
          console.error('清空点数据失败', error);
          message.error(`清空产品 ${productCode} 的模拟点数据失败，请稍后重试`);
        }
      }
    });
  };

  const refreshData = () => {
    if (propSimulations || propSimulationsWithCount) {
      onRefreshNeeded?.();
    } else {
      fetchSimulations();
    }
  };

  const getColumns = () => {
    const baseColumns = [
      { 
        title: '产品代码', 
        dataIndex: showHistoryMode ? ['priceSimulation', 'productCode'] : 'productCode', 
        key: 'productCode' 
      },
      { 
        title: '起始价格', 
        dataIndex: showHistoryMode ? ['priceSimulation', 'startPrice'] : 'startPrice', 
        key: 'startPrice' 
      },
      { 
        title: '目标价格', 
        dataIndex: showHistoryMode ? ['priceSimulation', 'finalPrice'] : 'finalPrice', 
        key: 'finalPrice' 
      },
    ];

    if (showHistoryMode) {
      baseColumns.push(
        {
          title: '状态',
          dataIndex: ['priceSimulation', 'isActive'],
          key: 'isActive',
          render: (isActive: boolean) => (
            <Tag color={isActive ? 'green' : 'default'}>
              {isActive ? '激活' : '未激活'}
            </Tag>
          )
        },
        {
          title: '关联点数据',
          dataIndex: 'pointCount',
          key: 'pointCount',
          render: (count: number) => `${count} 条`
        }
      );
    }

    baseColumns.push(
      { 
        title: '开始时间', 
        dataIndex: showHistoryMode ? ['priceSimulation', 'startTime'] : 'startTime', 
        key: 'startTime',
        render: (startTime: string) => (
          <TimeDisplay value={startTime} />
        )
      },
      { 
        title: '结束时间', 
        dataIndex: showHistoryMode ? ['priceSimulation', 'endTime'] : 'endTime', 
        key: 'endTime',
        render: (endTime: string) => (
          <TimeDisplay 
            value={endTime} 
            placeholder="进行中"
            type={endTime ? undefined : 'warning'}
          />
        )
      },
      {
        title: '操作',
        key: 'action',
        render: (_: any, record: any) => {
          const simulationData = showHistoryMode ? record.priceSimulation : record;
          const pointCount = showHistoryMode ? record.pointCount : 0;
          
          return (
            <Space>
              {onView && (
                <Button 
                  type="primary" 
                  size="small" 
                  onClick={() => onView(simulationData)}
                >
                  查看
                </Button>
              )}
              {showHistoryMode && simulationData.isActive && (
                <Button 
                  size="small" 
                  onClick={() => handleDeactivate(simulationData.productCode)}
                >
                  取消激活
                </Button>
              )}
              {showHistoryMode && pointCount > 0 && (
                <Button 
                  size="small" 
                  danger
                  onClick={() => handleClearPoints(simulationData.id, simulationData.productCode)}
                >
                  清空点数据
                </Button>
              )}
            </Space>
          );
        },
      }
    );

    return baseColumns;
  };

  const dataSource = showHistoryMode ? simulationsWithCount : simulations;
  const title = showHistoryMode ? "价格模拟历史记录" : "当前激活的价格模拟";

  return (
    <Table
      title={() => title}
      dataSource={dataSource}
      columns={getColumns()}
      rowKey={showHistoryMode ? (record: any) => record.priceSimulation.id : "id"}
      loading={loading && !propSimulations && !propSimulationsWithCount}
    />
  );
};

export default SimulationHistoryList;